import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { admin, type AdminUserRow } from "@/lib/admin";
import { AdminLayout } from "@/components/AdminLayout";
import { Card } from "@/components/Card";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Alert } from "@/components/Alert";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PromptDialog } from "@/components/PromptDialog";
import {
  useAdminRole, canMutateUsers, canDeleteUsers,
} from "@/hooks/useAdminRole";

// ────────────────────────────────────────────────────────────────────────
// Display helpers
// ────────────────────────────────────────────────────────────────────────

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Pick the most clinically relevant treatment for the row summary.
function activeTreatment(u: AdminUserRow) {
  return u.treatments.find((t) => t.status === "active") ?? u.treatments[0] ?? null;
}

type ProgramKey = "wl" | "trt" | "none";

function programKey(u: AdminUserRow): ProgramKey {
  const t = activeTreatment(u);
  if (!t) return "none";
  const m = t.medication.toLowerCase();
  if (m.includes("testosterone")) return "trt";
  if (m.includes("semaglutide") || m.includes("tirzepatide") || m.includes("liraglutide")) return "wl";
  return "none";
}

function programLabel(u: AdminUserRow): string {
  const t = activeTreatment(u);
  if (!t) return "—";
  const m = t.medication.toLowerCase();
  if (m.includes("semaglutide")) return "WL · Semaglutide";
  if (m.includes("tirzepatide")) return "WL · Tirzepatide";
  if (m.includes("testosterone")) return "TRT · Testosterone";
  return t.medication;
}

// Convert plan_duration_months → display label.
// 1 → "4 weeks", 3 → "3 months", 6 → "6 months". Anything else = raw.
type DurationKey = "1" | "3" | "6" | "other" | "none";

function durationKey(u: AdminUserRow): DurationKey {
  const t = activeTreatment(u);
  const d = t?.plan_duration_months ?? null;
  if (d === 1) return "1";
  if (d === 3) return "3";
  if (d === 6) return "6";
  if (d == null) return "none";
  return "other";
}

function durationLabel(u: AdminUserRow): string {
  const k = durationKey(u);
  if (k === "1") return "4 weeks";
  if (k === "3") return "3 months";
  if (k === "6") return "6 months";
  if (k === "other") return `${activeTreatment(u)?.plan_duration_months} mo`;
  return "—";
}

function monthlyPrice(u: AdminUserRow): number | null {
  return activeTreatment(u)?.monthly_price ?? null;
}

function fmtPrice(n: number | null): string {
  if (n == null) return "—";
  return `$${n.toFixed(0)}/mo`;
}

function fullName(u: AdminUserRow): string {
  return u.patient
    ? `${u.patient.first_name} ${u.patient.last_name}`.trim()
    : "—";
}

// ────────────────────────────────────────────────────────────────────────
// Sort + filter state
// ────────────────────────────────────────────────────────────────────────

type SortKey =
  | "created_desc" | "created_asc"
  | "signin_desc"  | "signin_asc"
  | "name_asc"     | "name_desc"
  | "price_desc"   | "price_asc";

const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
  { key: "created_desc", label: "Created (newest)" },
  { key: "created_asc",  label: "Created (oldest)" },
  { key: "signin_desc",  label: "Last sign-in (recent)" },
  { key: "signin_asc",   label: "Last sign-in (oldest)" },
  { key: "name_asc",     label: "Name (A → Z)" },
  { key: "name_desc",    label: "Name (Z → A)" },
  { key: "price_desc",   label: "Price (high → low)" },
  { key: "price_asc",    label: "Price (low → high)" },
];

function sortUsers(users: AdminUserRow[], key: SortKey): AdminUserRow[] {
  const arr = [...users];
  const toTime = (s: string | null) => (s ? new Date(s).getTime() : 0);
  arr.sort((a, b) => {
    switch (key) {
      case "created_desc": return toTime(b.created_at) - toTime(a.created_at);
      case "created_asc":  return toTime(a.created_at) - toTime(b.created_at);
      case "signin_desc":  return toTime(b.last_sign_in_at) - toTime(a.last_sign_in_at);
      case "signin_asc":   return toTime(a.last_sign_in_at) - toTime(b.last_sign_in_at);
      case "name_asc":     return fullName(a).localeCompare(fullName(b));
      case "name_desc":    return fullName(b).localeCompare(fullName(a));
      case "price_desc":   return (monthlyPrice(b) ?? -1) - (monthlyPrice(a) ?? -1);
      case "price_asc":    return (monthlyPrice(a) ?? Number.MAX_VALUE) - (monthlyPrice(b) ?? Number.MAX_VALUE);
    }
  });
  return arr;
}

// ────────────────────────────────────────────────────────────────────────
// Notice / pending action state
// ────────────────────────────────────────────────────────────────────────

type Notice = { tone: "success" | "error"; text: string } | null;

type Pending =
  | { action: "reset" | "magic" | "delete" | "unarchive"; user: AdminUserRow }
  | { action: "email" | "archive"; user: AdminUserRow }
  | null;

// ────────────────────────────────────────────────────────────────────────

export default function AdminUsers() {
  const qc = useQueryClient();
  const { role } = useAdminRole();
  const canMutate = canMutateUsers(role);
  const canDelete = canDeleteUsers(role);

  const [notice, setNotice] = useState<Notice>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [working, setWorking] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  // Filters
  const [q, setQ] = useState("");
  const [programFilter, setProgramFilter] = useState<"all" | ProgramKey>("all");
  const [durationFilter, setDurationFilter] = useState<"all" | DurationKey>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>("created_desc");

  const usersQ = useQuery({
    queryKey: ["admin", "users", showArchived],
    queryFn: () => admin.listUsers(showArchived),
  });

  const allUsers = usersQ.data?.users ?? [];

  // Apply filters + sort entirely client-side so toggling is instant.
  const filtered = useMemo(() => {
    let xs = allUsers;
    if (q.trim()) {
      const needle = q.toLowerCase();
      xs = xs.filter((u) => {
        const hay = `${u.email ?? ""} ${u.patient?.first_name ?? ""} ${u.patient?.last_name ?? ""} ${u.id}`.toLowerCase();
        return hay.includes(needle);
      });
    }
    if (programFilter !== "all") xs = xs.filter((u) => programKey(u) === programFilter);
    if (durationFilter !== "all") xs = xs.filter((u) => durationKey(u) === durationFilter);
    return sortUsers(xs, sortBy);
  }, [allUsers, q, programFilter, durationFilter, sortBy]);

  if (usersQ.isLoading) return <AdminLayout><LoadingScreen /></AdminLayout>;
  if (usersQ.error) {
    return (
      <AdminLayout>
        <Alert tone="error">{(usersQ.error as Error).message}</Alert>
      </AdminLayout>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // Mutations
  // ────────────────────────────────────────────────────────────────────────

  async function runPending() {
    if (!pending) return;
    if (pending.action === "email" || pending.action === "archive") return;
    setWorking(true);
    try {
      if (pending.action === "reset") {
        await admin.resetPassword(pending.user.id);
        setNotice({ tone: "success", text: `Password reset email sent to ${pending.user.email}` });
      } else if (pending.action === "magic") {
        await admin.sendMagicLink(pending.user.id);
        setNotice({ tone: "success", text: `Magic sign-in link sent to ${pending.user.email}` });
      } else if (pending.action === "delete") {
        await admin.deleteUser(pending.user.id);
        setNotice({ tone: "success", text: `Account ${pending.user.email} permanently deleted` });
        await qc.invalidateQueries({ queryKey: ["admin", "users"] });
      } else if (pending.action === "unarchive") {
        await admin.unarchiveUser(pending.user.id);
        setNotice({ tone: "success", text: `${pending.user.email} restored. Treatments stay paused — reactivate manually if appropriate.` });
        await qc.invalidateQueries({ queryKey: ["admin", "users"] });
      }
      setPending(null);
    } catch (e) {
      setNotice({ tone: "error", text: (e as Error).message });
    } finally {
      setWorking(false);
    }
  }

  async function runEmailChange(newEmail: string) {
    if (!pending || pending.action !== "email") return;
    setEmailError(null);
    if (!newEmail) { setEmailError("Email required"); return; }
    if (newEmail.toLowerCase() === (pending.user.email ?? "").toLowerCase()) {
      setPending(null);
      return;
    }
    setWorking(true);
    try {
      await admin.updateEmail(pending.user.id, newEmail);
      setNotice({
        tone: "success",
        text: `Email updated: ${pending.user.email} → ${newEmail}`,
      });
      await qc.invalidateQueries({ queryKey: ["admin", "users"] });
      setPending(null);
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.includes("email_in_use")) setEmailError("Another account is already using that email.");
      else if (msg.includes("invalid_email")) setEmailError("That email isn't formatted correctly.");
      else setEmailError(msg);
    } finally {
      setWorking(false);
    }
  }

  async function runArchive(reason: string) {
    if (!pending || pending.action !== "archive") return;
    setArchiveError(null);
    setWorking(true);
    try {
      await admin.archiveUser(pending.user.id, reason.trim() || undefined);
      setNotice({
        tone: "success",
        text: `${pending.user.email} archived. Login blocked, active treatments paused. Medical records retained per HIPAA.`,
      });
      await qc.invalidateQueries({ queryKey: ["admin", "users"] });
      setPending(null);
    } catch (e) {
      setArchiveError((e as Error).message);
    } finally {
      setWorking(false);
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <header className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-[28px] text-fh-ink leading-tight mb-1">Users</h1>
          <p className="text-[14px] text-fh-gray m-0">
            {filtered.length} shown · {allUsers.length} loaded
            {usersQ.data?.archived_count ? ` · ${usersQ.data.archived_count} archived hidden` : ""}
          </p>
        </div>
        <input
          type="search"
          placeholder="Search email, name, UID…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full sm:w-[280px] rounded-[10px] border border-fh-border bg-white px-3.5 py-2 text-[14px] placeholder:text-fh-gray-light focus:outline-none focus:border-fh-green focus-visible:ring-2 focus-visible:ring-fh-green/30"
        />
      </header>

      {/* Filter strip */}
      <div className="flex flex-wrap items-center gap-3 mb-4 text-[13px]">
        <FilterSelect
          label="Program"
          value={programFilter}
          onChange={(v) => setProgramFilter(v as typeof programFilter)}
          options={[
            { value: "all", label: "All programs" },
            { value: "wl", label: "Weight Loss" },
            { value: "trt", label: "TRT" },
            { value: "none", label: "No active program" },
          ]}
        />
        <FilterSelect
          label="Duration"
          value={durationFilter}
          onChange={(v) => setDurationFilter(v as typeof durationFilter)}
          options={[
            { value: "all", label: "Any duration" },
            { value: "1", label: "4 weeks" },
            { value: "3", label: "3 months" },
            { value: "6", label: "6 months" },
            { value: "none", label: "No plan" },
          ]}
        />
        <FilterSelect
          label="Sort by"
          value={sortBy}
          onChange={(v) => setSortBy(v as SortKey)}
          options={SORT_OPTIONS.map((s) => ({ value: s.key, label: s.label }))}
        />
        <label className="flex items-center gap-2 ml-1 cursor-pointer text-fh-ink select-none">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="accent-fh-green"
          />
          Show archived
        </label>
        {role === "viewer" && (
          <span className="text-[12px] text-fh-gray italic ml-2">
            Read-only access — contact a super admin to make changes.
          </span>
        )}
      </div>

      {notice && (
        <div className="mb-4">
          <Alert tone={notice.tone}>
            <div className="flex items-center justify-between gap-3">
              <span>{notice.text}</span>
              <button
                type="button"
                onClick={() => setNotice(null)}
                className="text-[12px] font-semibold underline"
              >
                Dismiss
              </button>
            </div>
          </Alert>
        </div>
      )}

      <Card>
        <div className="-mx-[22px] -my-[22px] overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead className="text-left text-[12px] uppercase tracking-wider text-fh-gray bg-fh-cream border-b border-fh-border">
              <tr>
                <th className="px-4 py-3 font-semibold">Patient</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Program</th>
                <th className="px-4 py-3 font-semibold">Plan</th>
                <th className="px-4 py-3 font-semibold">Price</th>
                <th className="px-4 py-3 font-semibold">Created</th>
                <th className="px-4 py-3 font-semibold">Last sign-in</th>
                <th className="px-4 py-3 font-semibold">UID</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-fh-gray">No users match the current filters.</td></tr>
              ) : (
                filtered.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    canMutate={canMutate}
                    canDelete={canDelete}
                    onReset={() => setPending({ action: "reset", user: u })}
                    onMagic={() => setPending({ action: "magic", user: u })}
                    onChangeEmail={() => { setEmailError(null); setPending({ action: "email", user: u }); }}
                    onArchive={() => { setArchiveError(null); setPending({ action: "archive", user: u }); }}
                    onUnarchive={() => setPending({ action: "unarchive", user: u })}
                    onDelete={() => setPending({ action: "delete", user: u })}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmDialog
        open={pending?.action === "reset"}
        title="Send password reset?"
        body={`A password reset email will be sent to ${pending?.user.email}. They'll be able to set a new password via that link.`}
        confirmLabel="Send reset email"
        loading={working}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending?.action === "magic"}
        title="Send magic sign-in link?"
        body={`A one-time sign-in link will be emailed to ${pending?.user.email}. Clicking it signs them in directly.`}
        confirmLabel="Send link"
        loading={working}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending?.action === "unarchive"}
        title="Restore archived account?"
        body={`${pending?.user.email} will be able to sign in again. Their paused treatments stay paused — reactivate them separately if appropriate.`}
        confirmLabel="Restore account"
        loading={working}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending?.action === "delete"}
        title="Permanently delete account?"
        body={`This permanently deletes ${pending?.user.email} and cascades to their patient record, treatments, shipments, and audit-trail patient_id links. This cannot be undone. For most cases, archive instead.`}
        confirmLabel="Delete forever"
        destructive
        loading={working}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      />
      <PromptDialog
        open={pending?.action === "email"}
        title="Change sign-in email"
        body={`Current: ${pending?.user.email ?? "—"}. The patient will sign in with the new email going forward; the change is audited.`}
        label="New email"
        initialValue={pending?.user.email ?? ""}
        type="email"
        placeholder="patient@example.com"
        confirmLabel="Update email"
        loading={working}
        error={emailError}
        onConfirm={runEmailChange}
        onCancel={() => { setPending(null); setEmailError(null); }}
      />
      <PromptDialog
        open={pending?.action === "archive"}
        title="Archive this account?"
        body={`${pending?.user.email} will be blocked from signing in. Their active treatments will be paused so they don't keep billing or shipping. Medical records are retained for the HIPAA-required retention period and can be restored at any time. Optionally record a reason for the audit trail.`}
        label="Reason (optional)"
        initialValue=""
        type="text"
        placeholder="e.g. Patient requested account closure"
        confirmLabel="Archive account"
        loading={working}
        error={archiveError}
        onConfirm={runArchive}
        onCancel={() => { setPending(null); setArchiveError(null); }}
      />
    </AdminLayout>
  );
}

// ────────────────────────────────────────────────────────────────────────
// FilterSelect — tiny labeled select wrapper for the toolbar.
// ────────────────────────────────────────────────────────────────────────

function FilterSelect({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="flex items-center gap-2 text-fh-gray">
      <span className="text-[12px] uppercase tracking-wider font-semibold">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-[8px] border border-fh-border bg-white px-2.5 py-1.5 text-[13px] text-fh-ink focus:outline-none focus:border-fh-green focus-visible:ring-2 focus-visible:ring-fh-green/30"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

// ────────────────────────────────────────────────────────────────────────
// UserRow — single row + floating actions menu.
// ────────────────────────────────────────────────────────────────────────

function UserRow({
  user, canMutate, canDelete,
  onReset, onMagic, onChangeEmail, onArchive, onUnarchive, onDelete,
}: {
  user: AdminUserRow;
  canMutate: boolean;
  canDelete: boolean;
  onReset: () => void;
  onMagic: () => void;
  onChangeEmail: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  function openMenu() {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuWidth = 224;
    const menuHeightEstimate = 180;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < menuHeightEstimate + 16;
    setMenuPos({
      top: openUp ? r.top - menuHeightEstimate - 4 : r.bottom + 4,
      left: Math.max(8, r.right - menuWidth),
    });
    setMenuOpen(true);
  }

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menuOpen]);

  async function copyUid() {
    await navigator.clipboard.writeText(user.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }

  const name = fullName(user);
  const phone = user.patient?.phone ?? user.phone ?? "—";
  const archived = !!user.patient?.archived_at;

  return (
    <tr className={`border-b border-fh-border last:border-0 ${archived ? "bg-fh-sand/50 text-fh-gray" : "hover:bg-fh-cream/60"}`}>
      <td className="px-4 py-3">
        <div className="font-semibold text-fh-ink flex items-center gap-2">
          {name}
          {archived && (
            <span className="text-[10.5px] font-semibold uppercase tracking-wider text-fh-gray bg-fh-border px-1.5 py-0.5 rounded">
              Archived
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-fh-ink">{user.email ?? "—"}</span>
          {user.email_confirmed_at ? (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-fh-green" title="Email confirmed" />
          ) : (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" title="Email not confirmed" />
          )}
        </div>
        <div className="text-[12px] text-fh-gray">{phone}</div>
      </td>
      <td className="px-4 py-3 text-fh-gray whitespace-nowrap">{programLabel(user)}</td>
      <td className="px-4 py-3 text-fh-gray whitespace-nowrap">{durationLabel(user)}</td>
      <td className="px-4 py-3 text-fh-ink whitespace-nowrap">{fmtPrice(monthlyPrice(user))}</td>
      <td className="px-4 py-3 text-fh-gray whitespace-nowrap">{relativeTime(user.created_at)}</td>
      <td className="px-4 py-3 text-fh-gray whitespace-nowrap">{relativeTime(user.last_sign_in_at)}</td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={copyUid}
          className="font-mono text-[12px] text-fh-gray hover:text-fh-ink"
          title="Copy UID"
        >
          {copied ? "copied ✓" : user.id.slice(0, 8) + "…"}
        </button>
      </td>
      <td className="px-4 py-3 text-right">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
          className="rounded-md px-2 py-1 text-fh-gray hover:bg-fh-border"
          aria-label="Actions"
          aria-haspopup="true"
          aria-expanded={menuOpen}
        >
          •••
        </button>
        {menuOpen && menuPos && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
            <div
              role="menu"
              className="fixed z-50 w-56 bg-white border border-fh-border rounded-[10px] shadow-card py-1 text-left"
              style={{ top: menuPos.top, left: menuPos.left }}
            >
              <MenuItem disabled={!canMutate || archived} onClick={() => { setMenuOpen(false); onReset(); }}>
                Send password reset
              </MenuItem>
              <MenuItem disabled={!canMutate || archived} onClick={() => { setMenuOpen(false); onMagic(); }}>
                Send magic sign-in link
              </MenuItem>
              <MenuItem disabled={!canMutate || archived} onClick={() => { setMenuOpen(false); onChangeEmail(); }}>
                Change email…
              </MenuItem>
              <div className="my-1 border-t border-fh-border" />
              {archived ? (
                <MenuItem disabled={!canMutate} onClick={() => { setMenuOpen(false); onUnarchive(); }}>
                  Restore from archive
                </MenuItem>
              ) : (
                <MenuItem disabled={!canMutate} onClick={() => { setMenuOpen(false); onArchive(); }}>
                  Archive account…
                </MenuItem>
              )}
              {canDelete && (
                <>
                  <div className="my-1 border-t border-fh-border" />
                  <MenuItem danger onClick={() => { setMenuOpen(false); onDelete(); }}>
                    Permanently delete…
                  </MenuItem>
                </>
              )}
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

function MenuItem({
  onClick, children, danger, disabled,
}: { onClick: () => void; children: React.ReactNode; danger?: boolean; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`block w-full text-left px-3.5 py-2 text-[13.5px] ${
        disabled
          ? "text-fh-gray-light cursor-not-allowed"
          : danger
          ? "text-red-600 hover:bg-red-50"
          : "text-fh-ink hover:bg-fh-cream"
      }`}
    >
      {children}
    </button>
  );
}
